/**
 * Field Extraction Utilities
 * Handles extracting and mapping fields from items
 */

import { ExportTemplate, TemplateField } from '@/types/exportTemplates';
import { extractStringIds } from '../core/formatting';
import { applyFieldExportMapping, LookupContext } from './references';
import { processFieldValue } from './processing';

/**
 * Map blueprint field to template field with type mapping
 * @param blueprintMultiple - The 'multiple' property from the blueprint field definition (takes precedence over field.multiple)
 */
export function mapBlueprintField(
  field: any,
  value: any,
  exportTemplate: ExportTemplate,
  stringIds?: string[],
  referenceData?: any,
  filterOptions?: string[],
  blueprintMultiple?: boolean,
  useTranslationKeys: boolean = true
): TemplateField {
  const fieldType = field.type || 'text';
  const mapping = exportTemplate.fieldMappings[fieldType] || { 
    typeName: 'string', 
    defaultValue: '""' 
  };
  
  // Debug logging for string_id
  if (stringIds && stringIds.length > 0) {
    console.log(`📝 Field "${field.label || field.name}" has string_ids:`, stringIds);
  }
  
  // Detect if this is a reference field
  const isReference = fieldType === 'types' || fieldType === 'tags' || fieldType === 'glossaryItem' || fieldType === 'dialog' || fieldType === 'document';
  const referenceType = isReference ? fieldType as ('types' | 'tags' | 'glossaryItem' | 'dialog' | 'document') : undefined;
  
  // Use blueprintMultiple (from blueprint definition) if provided, otherwise fall back to field.multiple
  const isMultiple = blueprintMultiple !== undefined ? blueprintMultiple : (field.multiple || false);
  
  if (isReference) {
    console.log(`🔗 Field "${field.label || field.name}" is a reference field (type: ${referenceType}), isMultiple: ${isMultiple}, filterOptions: ${JSON.stringify(filterOptions)}`);
  }
  
  // When useTranslationKeys is false (single-language mode), suppress translatable behavior
  // so fields export as plain strings instead of TranslationKey
  const isTranslatable = useTranslationKeys ? (field.trans || field.translation || false) : false;
  const effectiveStringIds = useTranslationKeys ? (stringIds && stringIds.length > 0 ? stringIds : undefined) : undefined;
  
  return {
    id: field.id,
    name: field.label || field.name || 'Field',
    label: field.label || field.name || 'Field',
    type: fieldType,
    mappedType: mapping.typeName,
    value: value,
    defaultValue: mapping.defaultValue,
    isRequired: field.req || field.required || false,
    isTranslatable: isTranslatable,
    isMultiple: isMultiple,
    comment: field.label || field.name || '',
    originalName: field.name,
    string_ids: effectiveStringIds, // Array of string_ids for translation lookups (suppressed in single-language mode)
    isReference: isReference,
    referenceType: referenceType,
    referenceData: referenceData,
    filterOptions: filterOptions || field.filterOptions // Allowed blueprint IDs from blueprint definition
  };
}

/**
 * Extract fields from an item, optionally flattening sections
 * 
 * @param item - The item to extract fields from
 * @param template - The export template with mappings
 * @param blueprints - Available blueprints
 * @param categoryName - Optional category name for per-field mapping lookup
 * @param lookupContext - Optional context for resolving reference IDs (types, tags, etc.)
 */
export function extractFields(
  item: any,
  template: ExportTemplate,
  blueprints: any[],
  categoryName?: string,
  lookupContext?: LookupContext,
  useTranslationKeys: boolean = true
): TemplateField[] {
  const fields: TemplateField[] = [];
  
  // Get categoryFieldMappings from template (if available)
  const categoryFieldMappings = (template as any).categoryFieldMappings as Record<string, Record<string, string>> | undefined;
  
  // Find the blueprint for this item to get field definitions (including filterOptions)
  const itemBlueprint = blueprints.find(bp => bp.id === item.blueprint_id);
  
  // Helper to get blueprint field definition properties
  // Blueprint data structure: sections are in blueprint.data.sections (newer) or blueprint.data (older format)
  // Returns: { filterOptions, multiple } from the blueprint field definition
  const getBlueprintFieldProps = (fieldId: string): { filterOptions?: string[], multiple?: boolean } => {
    if (!itemBlueprint || !itemBlueprint.data) return {};
    
    // Get sections data (newer format has nested sections, older format has sections directly in data)
    const sectionsData = (itemBlueprint.data as any).sections || itemBlueprint.data;
    
    if (sectionsData && typeof sectionsData === 'object') {
      // Iterate through all sections
      for (const sectionKey of Object.keys(sectionsData)) {
        // Skip metadata properties
        if (sectionKey.startsWith('_')) continue;
        
        const sectionData = sectionsData[sectionKey];
        if (!sectionData || typeof sectionData !== 'object') continue;
        
        // Check if this is a single-field section (has direct type property)
        if ('type' in sectionData && sectionData.id === fieldId) {
          console.log(`📋 Found blueprint field ${fieldId}: multiple=${sectionData.multiple}, filterOptions=${JSON.stringify(sectionData.filterOptions)}`);
          return {
            filterOptions: sectionData.filterOptions,
            multiple: sectionData.multiple
          };
        }
        
        // Check for multi-field sections - iterate through field properties
        for (const fieldKey of Object.keys(sectionData)) {
          if (fieldKey.startsWith('_')) continue;
          
          const fieldData = sectionData[fieldKey];
          if (fieldData && typeof fieldData === 'object' && 'type' in fieldData) {
            if (fieldData.id === fieldId || fieldKey === fieldId) {
              console.log(`📋 Found blueprint field ${fieldId}: multiple=${fieldData.multiple}, filterOptions=${JSON.stringify(fieldData.filterOptions)}`);
              return {
                filterOptions: fieldData.filterOptions,
                multiple: fieldData.multiple
              };
            }
          }
        }
      }
    }
    
    return {};
  };
  
  // Helper to get per-field export behavior
  const getFieldExportBehavior = (fieldId: string): string | undefined => {
    if (categoryFieldMappings && categoryName) {
      const categoryMappings = categoryFieldMappings[categoryName];
      if (categoryMappings && categoryMappings[fieldId]) {
        return categoryMappings[fieldId];
      }
    }
    return undefined;
  };

  // If ignoreSections is true, flatten all fields from all sections
  if (template.options?.ignoreSections && Array.isArray(item.data)) {
    item.data.forEach((section: any) => {
      if (section.fields && Array.isArray(section.fields)) {
        section.fields.forEach((field: any) => {
          console.log(`\n🔧 Processing field "${field.label || field.id}":`);
          console.log('  📦 Raw field.value:', JSON.stringify(field.value).substring(0, 150));
          
          // IMPORTANT: Extract string_ids BEFORE processing the value
          // because processing converts TranslatableValue objects to plain strings
          let stringIds = extractStringIds(field.value);

          if (stringIds.length === 0 && field.string_id) {
            console.log(`📎 Using field.string_id fallback for "${field.label}": ${field.string_id}`);
            stringIds = [field.string_id];
          }
          
          // Debug: Log if translatable field has no string_ids
          if ((field.trans || field.translation) && stringIds.length === 0) {
            console.warn(`⚠️ Translatable field "${field.label}" has no string_ids! field.string_id=${field.string_id}, field.value=`, field.value);
          }
          
          // Get per-field export behavior
          const fieldExportBehavior = getFieldExportBehavior(field.id);
          
          // Apply field export mapping to the value (with per-field support)
          const mappingResult = applyFieldExportMapping(
            field.value,
            field.type,
            field.id,
            categoryFieldMappings,
            categoryName,
            template.fieldExportMappings,
            lookupContext
          );
          
          // Process value based on options (e.g., strip HTML, text case)
          const mappedValue = processFieldValue(mappingResult.value, field.type, template.options, fieldExportBehavior);
          
          // Get properties from blueprint field definition (filterOptions, multiple)
          const blueprintProps = getBlueprintFieldProps(field.id);
          
          const templateField = mapBlueprintField(field, mappedValue, template, stringIds, mappingResult.referenceData, blueprintProps.filterOptions, blueprintProps.multiple, useTranslationKeys);
          fields.push(templateField);
        });
      }
    });
  } else {
    // Preserve section structure (existing behavior for other templates)
    if (Array.isArray(item.data)) {
      item.data.forEach((section: any) => {
        if (section.fields && Array.isArray(section.fields)) {
          section.fields.forEach((field: any) => {
            console.log(`\n🔧 Processing field "${field.label || field.id}":`);
            console.log('  📦 Raw field.value:', JSON.stringify(field.value).substring(0, 150));
            
            // IMPORTANT: Extract string_ids BEFORE processing the value
            // because processing converts TranslatableValue objects to plain strings
            let stringIds = extractStringIds(field.value);
            
            // Fallback: if no string_ids found in value, check field.string_id (singular)
            if (stringIds.length === 0 && field.string_id) {
              console.log(`📎 Using field.string_id fallback for "${field.label}": ${field.string_id}`);
              stringIds = [field.string_id];
            }
            
            // Debug: Log if translatable field has no string_ids
            if ((field.trans || field.translation) && stringIds.length === 0) {
              console.warn(`⚠️ Translatable field "${field.label}" has no string_ids! field.string_id=${field.string_id}, field.value=`, field.value);
            }
            
            // Get per-field export behavior
            const fieldExportBehavior = getFieldExportBehavior(field.id);
            
            // Apply field export mapping to the value (with per-field support)
            const mappingResult = applyFieldExportMapping(
              field.value,
              field.type,
              field.id,
              categoryFieldMappings,
              categoryName,
              template.fieldExportMappings,
              lookupContext
            );

            // Process value based on options (e.g., strip HTML, text case)
            const mappedValue = processFieldValue(mappingResult.value, field.type, template.options, fieldExportBehavior);
            
            // Get properties from blueprint field definition (filterOptions, multiple)
            const blueprintProps = getBlueprintFieldProps(field.id);
            
            const templateField = mapBlueprintField(field, mappedValue, template, stringIds, mappingResult.referenceData, blueprintProps.filterOptions, blueprintProps.multiple, useTranslationKeys);
            fields.push(templateField);
          });
        }
      });
    }
  }
  
  return fields;
}
